import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { IconButton, Tooltip } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useMutation } from "react-query";
import { useTranslation } from "react-i18next";
import { getDashboardMetricInsights } from "../../../api/client";
import MetricAiInsightsDialog from "../../../components/MetricAiInsightsDialog";

const FinanceMetricAiContext = createContext(null);

const SCOPE = "finance";

/**
 * Provider de explicação por IA para o console Financeiro (mesmo padrão do dashboard).
 * period: { start, end } ou string YYYY-MM / YYYY
 * summaryContext: objeto livre com snapshot resumido da aba
 */
export function FinanceMetricAiProvider({ children, period, summaryContext }) {
  const [open, setOpen] = useState(false);
  const [metric, setMetric] = useState("");
  const [metricName, setMetricName] = useState("");
  const [metricLabel, setMetricLabel] = useState("");
  const [metricHuman, setMetricHuman] = useState("");
  const [basePayload, setBasePayload] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const lastCfgRef = useRef(null);

  const resolvedPeriod = useMemo(() => {
    if (period && typeof period === "object" && (period.start || period.end)) {
      return { start: period.start || null, end: period.end || null };
    }
    const s = period != null ? String(period) : "";
    if (/^\d{4}-\d{2}$/.test(s)) return { start: `${s}-01`, end: s };
    if (/^\d{4}$/.test(s)) return { start: `${s}-01-01`, end: `${s}-12-31` };
    return { start: s || null, end: s || null };
  }, [period]);

  const mutation = useMutation(
    async (cfg) => {
      const payload = {
        metric: cfg.metric,
        metricName: cfg.metricName,
        metricDefinition: cfg.metricDefinition,
        metricLabel: cfg.metricLabel || cfg.metricName,
        metricValue: cfg.metricValue,
        metricSec: cfg.metricSec,
        metricHuman: cfg.metricHuman,
        period: resolvedPeriod,
        summaryContext: {
          ...(summaryContext || {}),
          ...(cfg.summaryContext || {}),
          module: "platform_finance",
        },
        filters: cfg.filters || { module: "finance" },
      };
      return getDashboardMetricInsights(SCOPE, payload);
    },
    {
      onSuccess: (res) => {
        setData(res || null);
        setError(null);
      },
      onError: (err) => {
        const msg =
          (typeof err?.response?.data?.message === "string" && err.response.data.message) ||
          (typeof err?.message === "string" && err.message) ||
          "Não foi possível gerar a explicação da IA no momento.";
        setError(msg);
      },
    }
  );

  const openAi = useCallback(
    (cfg) => {
      if (!cfg?.metric) return;
      setMetric(cfg.metric);
      setMetricName(cfg.metricName || cfg.metric);
      setMetricLabel(cfg.metricLabel || cfg.metricName || cfg.metric);
      setMetricHuman(cfg.metricHuman || "");
      setBasePayload(cfg);
      setOpen(true);
      setData(null);
      setError(null);
      lastCfgRef.current = cfg;
      mutation.mutate(cfg);
    },
    [mutation]
  );

  const value = useMemo(
    () => ({
      openAi,
      period: resolvedPeriod,
      summaryContext,
    }),
    [openAi, resolvedPeriod, summaryContext]
  );

  return (
    <FinanceMetricAiContext.Provider value={value}>
      {children}
      <MetricAiInsightsDialog
        open={open}
        metric={metric}
        metricName={metricName}
        metricLabel={metricLabel}
        metricHuman={metricHuman}
        period={resolvedPeriod}
        loading={mutation.isLoading}
        error={error}
        data={data}
        onClose={() => setOpen(false)}
        scope={SCOPE}
        basePayload={{
          ...(basePayload || {}),
          period: resolvedPeriod,
          summaryContext: {
            ...(summaryContext || {}),
            ...(basePayload?.summaryContext || {}),
            module: "platform_finance",
          },
          filters: basePayload?.filters || { module: "finance" },
        }}
      />
    </FinanceMetricAiContext.Provider>
  );
}

export function useFinanceMetricAi() {
  return useContext(FinanceMetricAiContext);
}

/** Botão ★ IA no padrão do dashboard (chat.json analytics.explainAi). */
export function FinanceAiIconButton({ cfg, size = "small", fontSize = "inherit", sx }) {
  const { t } = useTranslation("chat");
  const ctx = useFinanceMetricAi();
  if (!ctx?.openAi || !cfg?.metric) return null;
  return (
    <Tooltip title={t("analytics.explainAi")}>
      <IconButton
        size={size}
        onClick={(e) => {
          e.stopPropagation();
          ctx.openAi(cfg);
        }}
        aria-label={t("analytics.explainAiAria")}
        sx={sx}
      >
        <AutoAwesomeIcon fontSize={fontSize} />
      </IconButton>
    </Tooltip>
  );
}

/**
 * Monta cfg padrão a partir de título/valor.
 * Uso: aiCfg({ metric: 'finance_dre_revenue', name: 'Receita', value, definition })
 */
export function financeAiCfg({
  metric,
  name,
  label,
  value,
  human,
  definition,
  summaryContext,
  filters,
}) {
  return {
    metric: String(metric || "")
      .toLowerCase()
      .trim()
      .slice(0, 64),
    metricName: name || label || metric,
    metricLabel: label || name || metric,
    metricDefinition:
      definition ||
      `Métrica financeira do console da plataforma Xpro: ${name || label || metric}.`,
    metricValue: value,
    metricHuman: human != null ? String(human) : value != null ? String(value) : "",
    summaryContext,
    filters,
  };
}
